// Localização: frontend/src/pages/Financeiro/HistoricoTransacoesPage.jsx
// Página de histórico unificado de transações financeiras

import React, { useState, useEffect, useRef } from 'react';
import ModalTransferencia from '../../components/Financeiro/ModalTransferencia';
import ModalMesclarContas from '../../components/Financeiro/ModalMesclarContas';
import PrimaryButton from '../../components/Common/PrimaryButton';
import AcessoRapidoFinanceiro from '../../components/Financeiro/AcessoRapidoFinanceiro';
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import financeiroApi from '../../api/financeiroApi';
import {
    History,
    Filter,
    Search,
    X,
    ChevronDown,
    ChevronUp,
    Merge,
    Trash2,
    Eye,
    ArrowRightLeft,
    Calendar
} from 'lucide-react';

const HistoricoTransacoesPage = () => {
    const [transacoes, setTransacoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [contas, setContas] = useState([]);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [modalTransferenciaAberto, setModalTransferenciaAberto] = useState(false);
    const [saldoFinal, setSaldoFinal] = useState(0);
    const [periodoFluxo, setPeriodoFluxo] = useState('mes');
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);

    // Filtros
    const [filtros, setFiltros] = useState({
        conta_bancaria_id: '',
        data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // Primeiro dia do mês atual
        data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0] // Último dia do mês atual
    });

    const [paginacao, setPaginacao] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1
    });

    // Carregar contas bancárias para o filtro
    useEffect(() => {
        const carregarContas = async () => {
            try {
                const response = await financeiroApi.contaBancaria.getAll();
                setContas(response.data?.data || response.data || []);
            } catch (err) {
                console.error('Erro ao carregar contas:', err);
            }
        };
        carregarContas();
    }, []);

    // Carregar transações
    const carregarTransacoes = async () => {
        setLoading(true);
        try {
            const params = {
                page: paginacao.page,
                limit: paginacao.limit,
                exclude_status: 'pendente,cancelado', // Exclude incomplete transactions
                ...filtros
            };

            // Remover campos vazios
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await financeiroApi.transacao.getAll(params);
            const dados = response.data;

            setTransacoes(dados.data || []);
            if (dados.meta) {
                setSaldoFinal(dados.meta.saldo_atual || 0);
            }
            if (dados.pagination) {
                setPaginacao(prev => ({
                    ...prev,
                    total: dados.pagination.total,
                    totalPages: dados.pagination.totalPages
                }));
            }
        } catch (err) {
            console.error('Erro ao carregar transações:', err);
            setError('Erro ao carregar histórico de transações');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarTransacoes();
    }, [filtros, paginacao.page]);

    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
        // Resetar para página 1 ao filtrar
        setPaginacao(prev => ({ ...prev, page: 1 }));
    };

    const handleDateRangeChange = (start, end) => {
        setFiltros(prev => ({
            ...prev,
            data_inicio: start,
            data_fim: end
        }));
        setPaginacao(prev => ({ ...prev, page: 1 }));
    };

    const handlePeriodoChange = (periodo) => {
        setPeriodoFluxo(periodo);
        const today = new Date();
        const start = new Date();
        const end = new Date();

        if (periodo === 'dia') {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        } else if (periodo === 'semana') {
            const dayOfWeek = today.getDay();
            start.setDate(today.getDate() - dayOfWeek);
            end.setDate(start.getDate() + 6);
        } else if (periodo === 'mes') {
            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
        }

        setFiltros(prev => ({
            ...prev,
            data_inicio: start.toISOString().split('T')[0],
            data_fim: end.toISOString().split('T')[0]
        }));
        setPaginacao(prev => ({ ...prev, page: 1 }));
    };

    const handleTransferencia = async (dados) => {
        try {
            await financeiroApi.transferencia.create(dados);
            setSuccess('Transferência realizada com sucesso!');
            setModalTransferenciaAberto(false);
            carregarTransacoes();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao realizar transferência');
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const formatarData = (data) => {
        if (!data) return '-';
        return new Date(data).toLocaleDateString('pt-BR');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pago':
            case 'recebido':
                return 'bg-green-100 text-green-800';
            case 'pendente':
                return 'bg-yellow-100 text-yellow-800';
            case 'atrasado':
                return 'bg-red-100 text-red-800';
            case 'cancelado':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    return (
        <div className="min-h-screen w-full">
            {/* Acesso Rápido às Funções - Posição Fixa - Centralizado - Abaixo do Header */}
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20 lg:top-20">
                <AcessoRapidoFinanceiro />
            </div>

            {/* Botão Flutuante à Direita - Período */}
            <div className="fixed top-20 right-4 sm:right-6 lg:right-8 z-30">
                <div ref={periodButtonRef}>
                    <button
                        onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                        className={`
                            flex items-center justify-center gap-2
                            px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                            transition-all duration-200 font-medium 
                            text-xs sm:text-sm
                            border shadow-lg hover:shadow-xl
                            backdrop-blur-sm
                            ${showPeriodFilter 
                                ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                        `}
                        title={showPeriodFilter ? 'Ocultar filtro de período' : 'Mostrar filtro de período'}
                    >
                        <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showPeriodFilter ? 'scale-110' : ''} transition-transform`} />
                        <span className="hidden sm:inline whitespace-nowrap">Período</span>
                    </button>
                </div>
            </div>

            {/* Filtro de Calendário Flutuante - Dropdown */}
            {showPeriodFilter && (
                <div ref={periodFilterRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:max-w-md">
                    <FloatingCalendarFilter
                        periodo={periodoFluxo}
                        onPeriodoChange={handlePeriodoChange}
                        startDate={filtros.data_inicio}
                        endDate={filtros.data_fim}
                        onDateRangeChange={handleDateRangeChange}
                        noNegativeMargin={true}
                        customZIndex="z-40"
                        showOnlyContent={true}
                    />
                </div>
            )}

            {/* Conteúdo com espaçamento para o AcessoRapido fixo */}
            <div className="pt-32 pb-8 px-3 sm:px-6 lg:px-10 space-y-4 sm:space-y-6">

            {/* Botão Nova Transferência e Filtro de Conta */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                {/* Filtro de Conta Bancária */}
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200 flex-1 sm:flex-initial sm:w-auto w-full">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Conta Bancária
                    </label>
                    <div className="relative">
                        <select
                            name="conta_bancaria_id"
                            value={filtros.conta_bancaria_id}
                            onChange={handleFiltroChange}
                            className="w-full pl-3 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-300 bg-white transition-colors"
                        >
                            <option value="">Todas as contas</option>
                            {contas.map(conta => (
                                <option key={conta.id} value={conta.id}>
                                    {conta.nome}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Botão Nova Transferência */}
                <div className="flex justify-end sm:justify-start">
                    <PrimaryButton
                        onClick={() => setModalTransferenciaAberto(true)}
                        className="flex items-center gap-2 w-full sm:w-auto text-sm sm:text-base"
                    >
                        <ArrowRightLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Nova Transferência</span>
                        <span className="sm:hidden">Transferência</span>
                    </PrimaryButton>
                </div>
            </div>

            {/* Mensagens de Feedback */}
            {success && (
                <div className="bg-green-50 text-green-700 p-3 sm:p-4 rounded-lg border border-green-200 text-sm">
                    {success}
                </div>
            )}
            {error && (
                <div className="bg-red-50 text-red-700 p-3 sm:p-4 rounded-lg border border-red-200 text-sm">
                    {error}
                </div>
            )}

            {/* Tabela Desktop / Cards Mobile */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Versão Desktop - Tabela */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conta</th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-4 lg:px-6 py-10 text-center text-gray-500">
                                        Carregando transações...
                                    </td>
                                </tr>
                            ) : transacoes.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 lg:px-6 py-10 text-center text-gray-500">
                                        Nenhuma transação encontrada no período.
                                    </td>
                                </tr>
                            ) : (
                                transacoes.map((transacao) => (
                                    <tr key={transacao.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatarData(transacao.data_transacao)}
                                        </td>
                                        <td className="px-4 lg:px-6 py-4 text-sm text-gray-900">
                                            <div className="font-medium">{transacao.descricao}</div>
                                            {transacao.cliente_fornecedor && (
                                                <div className="text-xs text-gray-500">{transacao.cliente_fornecedor}</div>
                                            )}
                                        </td>
                                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${transacao.categoria?.cor}20`,
                                                    color: transacao.categoria?.cor
                                                }}
                                            >
                                                {transacao.categoria?.nome || '-'}
                                            </span>
                                        </td>
                                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {transacao.conta_bancaria?.nome || '-'}
                                        </td>
                                        <td className={`px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium ${transacao.tipo === 'Receita' ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {transacao.tipo === 'Receita' ? '+' : '-'}{formatarMoeda(transacao.valor)}
                                        </td>
                                        <td className={`px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium ${transacao.saldo >= 0 ? 'text-blue-600' : 'text-red-600'
                                            }`}>
                                            {formatarMoeda(transacao.saldo)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-gray-50 font-medium">
                            <tr>
                                <td colSpan="5" className="px-4 lg:px-6 py-4 text-right text-sm text-gray-900">
                                    Saldo Final do Período:
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatarMoeda(saldoFinal)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Versão Mobile - Cards */}
                <div className="md:hidden">
                    {loading ? (
                        <div className="p-6 text-center text-gray-500">
                            Carregando transações...
                        </div>
                    ) : transacoes.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            Nenhuma transação encontrada no período.
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {transacoes.map((transacao) => (
                                <div key={transacao.id} className="p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm text-gray-900 truncate">
                                                {transacao.descricao}
                                            </div>
                                            {transacao.cliente_fornecedor && (
                                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                    {transacao.cliente_fornecedor}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`ml-2 text-right text-sm font-semibold ${transacao.tipo === 'Receita' ? 'text-green-600' : 'text-red-600'}`}>
                                            {transacao.tipo === 'Receita' ? '+' : '-'}{formatarMoeda(transacao.valor)}
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                                        <div className="text-gray-500">
                                            {formatarData(transacao.data_transacao)}
                                        </div>
                                        
                                        {transacao.categoria?.nome && (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${transacao.categoria?.cor}20`,
                                                    color: transacao.categoria?.cor
                                                }}
                                            >
                                                {transacao.categoria.nome}
                                            </span>
                                        )}
                                        
                                        {transacao.conta_bancaria?.nome && (
                                            <div className="text-gray-500">
                                                • {transacao.conta_bancaria.nome}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-500">Saldo:</span>
                                            <span className={`font-medium ${transacao.saldo >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                {formatarMoeda(transacao.saldo)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Saldo Final Mobile */}
                    {transacoes.length > 0 && (
                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">Saldo Final do Período:</span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {formatarMoeda(saldoFinal)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Paginação */}
                {paginacao.totalPages > 1 && (
                    <div className="bg-white px-3 sm:px-4 lg:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
                        {/* Informações - Mobile */}
                        <div className="w-full sm:hidden text-center">
                            <p className="text-xs text-gray-700">
                                Página <span className="font-medium">{paginacao.page}</span> de <span className="font-medium">{paginacao.totalPages}</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {(paginacao.page - 1) * paginacao.limit + 1} - {Math.min(paginacao.page * paginacao.limit, paginacao.total)} de {paginacao.total} resultados
                            </p>
                        </div>
                        
                        {/* Informações - Desktop */}
                        <div className="hidden sm:block flex-1">
                            <p className="text-sm text-gray-700">
                                Mostrando <span className="font-medium">{(paginacao.page - 1) * paginacao.limit + 1}</span> a <span className="font-medium">{Math.min(paginacao.page * paginacao.limit, paginacao.total)}</span> de <span className="font-medium">{paginacao.total}</span> resultados
                            </p>
                        </div>
                        
                        {/* Controles de Paginação */}
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                            <button
                                onClick={() => setPaginacao(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                                disabled={paginacao.page === 1}
                                className="relative inline-flex items-center px-3 sm:px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <span className="sr-only">Anterior</span>
                                <ChevronDown className="h-5 w-5 transform rotate-90" />
                                <span className="ml-1 sm:hidden">Anterior</span>
                            </button>
                            <span className="text-sm text-gray-700 px-2 sm:hidden">
                                {paginacao.page} / {paginacao.totalPages}
                            </span>
                            <button
                                onClick={() => setPaginacao(prev => ({ ...prev, page: Math.min(prev.page + 1, paginacao.totalPages) }))}
                                disabled={paginacao.page === paginacao.totalPages}
                                className="relative inline-flex items-center px-3 sm:px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <span className="sr-only">Próxima</span>
                                <span className="mr-1 sm:hidden">Próxima</span>
                                <ChevronDown className="h-5 w-5 transform -rotate-90" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ModalTransferencia
                isOpen={modalTransferenciaAberto}
                onClose={() => setModalTransferenciaAberto(false)}
                onConfirm={handleTransferencia}
            />

            <ModalMesclarContas />
            </div>
        </div>
    );
};

export default HistoricoTransacoesPage;
